// HomePage.js — Dashboard with left sidebar navigation and configurable metric tiles
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle, BarChart3, Boxes, Car, ChevronRight, ClipboardList,
  FileText, Layers, ListChecks, Package, PlusCircle, ScanLine, Settings,
  Shield, Truck, Upload, Users, Warehouse, BadgeDollarSign, X,
} from 'lucide-react';
import { api } from '../scripts/api';

// ── Sidebar navigation definition ─────────────────────────────────────────

const NAV_GROUPS = [
  {
    label: 'Inventory',
    items: [
      { to: '/inventory', label: 'All Records',   icon: Boxes },
      { to: '/demo',      label: 'Demo Fleet',    icon: Car },
      { to: '/stock',     label: 'Stock',         icon: Warehouse },
      { to: '/rentals',   label: 'Rentals',       icon: BadgeDollarSign },
      { to: '/sold',      label: 'Sold',          icon: ListChecks },
      { to: '/admin/customers/details', label: 'Customers', icon: Users },
    ],
  },
  {
    label: 'PDI',
    items: [
      { to: '/pdi',          label: 'Start PDI',   icon: ClipboardList },
      { to: '/pdi/manage',   label: 'Sessions',    icon: FileText },
      { to: '/pdi/reports',  label: 'PDI Reports', icon: BarChart3 },
      { to: '/pdi/templates',label: 'Templates',   icon: Layers },
    ],
  },
  {
    label: 'Tools',
    items: [
      { to: '/new',     label: 'Add Record',  icon: PlusCircle },
      { to: '/vin',     label: 'Scan VIN',    icon: ScanLine },
      { to: '/reports', label: 'Analytics',   icon: BarChart3 },
      { to: '/driver',  label: 'Driver',      icon: Truck },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/admin/import', label: 'Import',   icon: Upload },
      { to: '/admin/users',  label: 'Users',    icon: Users },
      { to: '/admin',        label: 'Settings', icon: Shield },
    ],
  },
];

// ── Metric definitions ─────────────────────────────────────────────────────
// Each metric knows where to get its value from the API response,
// what color to use, and where clicking the tile navigates.

const ALL_METRICS = [
  { id: 'total',         label: 'Total Inventory',    desc: 'All active units',            color: '#3b82f6', to: '/inventory' },
  { id: 'alerts',        label: 'Alerts',             desc: 'Units flagged for attention',  color: '#ef4444', to: '/inventory?color=6' },
  { id: 'in_stock',      label: 'In Stock',           desc: 'Available inventory',          color: '#22c55e', to: '/stock' },
  { id: 'demo',          label: 'Demo Fleet',         desc: 'Active demo units',            color: '#a855f7', to: '/demo' },
  { id: 'rentals',       label: 'Rentals',            desc: 'Rental fleet',                 color: '#f97316', to: '/rentals' },
  { id: 'sold',          label: 'Sold',               desc: 'Closed deals',                 color: '#10b981', to: '/sold' },
  { id: 'on_order',      label: 'On Order',           desc: 'Ordered, not yet received',    color: '#06b6d4', to: '/inventory?color=8' },
  { id: 'to_order',      label: 'To Order',           desc: 'Pending order creation',       color: '#eab308', to: '/inventory?color=7' },
  { id: 'in_transit',    label: 'In Transit',         desc: 'Units currently moving',       color: '#f59e0b', to: '/inventory?color=4,5,13' },
  { id: 'ready_factory', label: 'Ready at Factory',   desc: 'Awaiting pickup',              color: '#64748b', to: '/inventory?color=12' },
  { id: 'pdi_stock',     label: 'PDI / In Stock',     desc: 'Stock & PDI — Calgary',        color: '#0d6efd', to: '/inventory?color=9' },
  { id: 'chassis_alloc', label: 'Chassis Allocated',  desc: 'Allocation slots',             color: '#94a3b8', to: '/inventory?color=11' },
  { id: 'components',    label: 'Components',         desc: 'Total tracked components',     color: '#6366f1', to: '/inventory' },
  { id: 'unassigned',    label: 'Unassigned',         desc: 'No status set',                color: '#9ca3af', to: '/inventory?color=0' },
];

const DEFAULT_METRIC_IDS = [
  'total', 'alerts', 'in_stock', 'demo', 'rentals',
  'sold', 'on_order', 'in_transit', 'ready_factory', 'pdi_stock',
];

const STORAGE_KEY = 'home_metrics_v1';
const MAX_METRICS = 10;

function loadSavedMetrics() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const ids = JSON.parse(raw);
      if (Array.isArray(ids) && ids.length > 0) return ids.slice(0, MAX_METRICS);
    }
  } catch (_) {}
  return DEFAULT_METRIC_IDS;
}

function computeValue(id, summary, pgStats) {
  const byColor = {};
  (summary?.byColor || []).forEach(({ color_code, n }) => { byColor[String(color_code)] = Number(n); });

  switch (id) {
    case 'total':         return summary?.total ?? null;
    case 'alerts':        return summary?.flags?.alerts ?? byColor['6'] ?? null;
    case 'in_stock':      return summary?.flags?.is_stock ?? null;
    case 'demo':          return summary?.flags?.is_demo ?? byColor['10'] ?? null;
    case 'rentals':       return summary?.flags?.is_rental ?? byColor['2'] ?? null;
    case 'sold':          return byColor['1'] ?? null;
    case 'on_order':      return byColor['8'] ?? null;
    case 'to_order':      return byColor['7'] ?? null;
    case 'in_transit':    return (byColor['4'] ?? 0) + (byColor['5'] ?? 0) + (byColor['13'] ?? 0);
    case 'ready_factory': return byColor['12'] ?? null;
    case 'pdi_stock':     return byColor['9'] ?? null;
    case 'chassis_alloc': return byColor['11'] ?? null;
    case 'components':    return pgStats?.total_components ?? null;
    case 'unassigned':    return byColor['0'] ?? null;
    default:              return null;
  }
}

// ── Sidebar ────────────────────────────────────────────────────────────────

function Sidebar() {
  const nav = useNavigate();
  const loc = useLocation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      <div style={{ fontWeight: 900, fontSize: 15, letterSpacing: 0.3, padding: '4px 6px 12px' }}>
        SNA Inventory
      </div>

      <nav className="dash-nav" style={{ flex: 1, overflowY: 'auto' }}>
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="dash-section">{group.label}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = loc.pathname === item.to || (item.to !== '/home' && loc.pathname.startsWith(item.to) && item.to !== '/');
              return (
                <button
                  key={item.to + item.label}
                  className={`dash-navbtn${active ? ' active' : ''}`}
                  onClick={() => nav(item.to)}
                  style={{ marginTop: 3 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Icon size={15} style={{ opacity: 0.75, flexShrink: 0 }} />
                    <span className="dash-navtitle">{item.label}</span>
                  </span>
                  <ChevronRight size={13} style={{ opacity: 0.3 }} />
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 10, marginTop: 8, fontSize: 11, opacity: 0.35, textAlign: 'center' }}>
        SNA IMS
      </div>
    </div>
  );
}

// ── Metric tile ────────────────────────────────────────────────────────────

function MetricTile({ def, value, loading }) {
  const nav = useNavigate();
  const isNull = value === null || value === undefined;

  return (
    <button
      onClick={() => nav(def.to)}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 14,
        padding: '18px 20px 16px',
        textAlign: 'left',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 120ms, box-shadow 120ms',
        minWidth: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {/* Color accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: def.color, borderRadius: '14px 14px 0 0' }} />

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, marginBottom: 10 }}>
        {def.label}
      </div>

      <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: def.color, letterSpacing: -1 }}>
        {loading ? <span style={{ fontSize: 22, opacity: 0.4 }}>—</span> : isNull ? <span style={{ fontSize: 20, opacity: 0.3 }}>—</span> : value.toLocaleString()}
      </div>

      <div style={{ fontSize: 11, opacity: 0.45, marginTop: 6 }}>{def.desc}</div>
    </button>
  );
}

// ── Metrics config modal ───────────────────────────────────────────────────

function MetricsModal({ selected, onSave, onClose }) {
  const [draft, setDraft] = useState(selected);

  const toggle = (id) => {
    setDraft((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_METRICS) return prev; // max reached
      return [...prev, id];
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 16, width: 460, maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Dashboard Metrics</div>
            <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2 }}>
              {draft.length} / {MAX_METRICS} selected
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={18} /></button>
        </div>

        {/* Metric list */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '10px 20px' }}>
          {ALL_METRICS.map((def) => {
            const checked = draft.includes(def.id);
            const disabled = !checked && draft.length >= MAX_METRICS;
            return (
              <label
                key={def.id}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--card-border)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }}
              >
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(def.id)} style={{ width: 16, height: 16, accentColor: def.color }} />
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: def.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{def.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.55, marginTop: 1 }}>{def.desc}</div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--card-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => { onSave(draft); onClose(); }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Main dashboard page ────────────────────────────────────────────────────

export default function HomePage() {
  const [selectedIds, setSelectedIds] = useState(loadSavedMetrics);
  const [summary, setSummary] = useState(null);
  const [pgStats, setPgStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.allSettled([
        api.get('/reports/summary?table=all_inventory'),
        api.get('/pg/stats'),
      ]);
      if (s.status === 'fulfilled') setSummary(s.value.data);
      if (p.status === 'fulfilled') setPgStats(p.value.data);
      setLastRefresh(new Date());
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleSave = (ids) => {
    setSelectedIds(ids);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  };

  const activeDefs = selectedIds
    .map((id) => ALL_METRICS.find((m) => m.id === id))
    .filter(Boolean);

  const sidebar = <Sidebar />;

  return (
    <div className="container" style={{ paddingTop: 12 }}>
      <div className="dash-shell">
        {/* ── Left sidebar ── */}
        <aside className="dash-left">
          {sidebar}
        </aside>

        {/* ── Main content ── */}
        <main className="dash-right">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: -0.3 }}>Dashboard</div>
              {lastRefresh && (
                <div style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>
                  Updated {lastRefresh.toLocaleTimeString()}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-outline" style={{ fontSize: 12, padding: '6px 12px' }} onClick={fetchMetrics} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
              <button
                className="btn-outline"
                style={{ padding: '6px 10px' }}
                onClick={() => setConfigOpen(true)}
                title="Configure metrics"
              >
                <Settings size={15} />
              </button>
            </div>
          </div>

          {/* Metric tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 12, marginBottom: 20 }}>
            {activeDefs.map((def) => (
              <MetricTile
                key={def.id}
                def={def}
                value={computeValue(def.id, summary, pgStats)}
                loading={loading}
              />
            ))}
          </div>

          {/* Quick links section */}
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.4, marginBottom: 10 }}>
              Quick Actions
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'Add Record', to: '/new' },
                { label: 'Scan VIN',   to: '/vin' },
                { label: 'Start PDI',  to: '/pdi' },
                { label: 'Import',     to: '/admin/import' },
                { label: 'Reports',    to: '/reports' },
              ].map((q) => (
                <NavQuickBtn key={q.to} label={q.label} to={q.to} />
              ))}
            </div>
          </div>
        </main>
      </div>

      {configOpen && (
        <MetricsModal
          selected={selectedIds}
          onSave={handleSave}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}

function NavQuickBtn({ label, to }) {
  const nav = useNavigate();
  return (
    <button className="btn-outline" style={{ fontSize: 13, padding: '7px 14px' }} onClick={() => nav(to)}>
      {label}
    </button>
  );
}
